import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getSchedules } from 'src/api/getSchedules/getSchedules';
import { ScheduleStatus } from 'src/api/getSchedules/types';
import { EVENT_TYPES } from 'src/api/getEvents';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';

/**
 * A venue is unavailable if it already has a scheduled pricing publish within
 * this many minutes before or after the requested publication time.
 */
export const CONFLICT_WINDOW_MINUTES = 60;

// Large limit so the window is typically covered in a single page; we still
// paginate below to stay correct when a window contains more than this.
const CONFLICT_PAGE_LIMIT = '100';

// Stable empty reference so consumers' memos don't churn while loading/disabled.
const NO_CONFLICTS = new Set<string>();

const fetchConflictingVenueIds = async (scheduledFrom: string, scheduledTo: string): Promise<Set<string>> => {
  const windowStart = dayjs(scheduledFrom);
  const windowEnd = dayjs(scheduledTo);

  // A schedule conflicts only if its scheduled_for falls inside the window
  // (inclusive). We filter client-side rather than trusting that every
  // returned schedule honours the scheduled_from / scheduled_to params.
  const isWithinWindow = (scheduledFor: string): boolean => {
    const firesAt = dayjs(scheduledFor);
    return !firesAt.isBefore(windowStart) && !firesAt.isAfter(windowEnd);
  };

  const conflictingVenueIds = new Set<string>();
  let afterId: string | undefined;

  do {
    const page = await getSchedules(CONFLICT_PAGE_LIMIT, afterId, ScheduleStatus.SCHEDULED, scheduledFrom, scheduledTo);

    page.data
      .filter(
        (schedule) => schedule.event_type === EVENT_TYPES.PUBLISH_PRICING && isWithinWindow(schedule.scheduled_for),
      )
      .forEach((schedule) => schedule.venues.forEach((venue) => conflictingVenueIds.add(venue.venue_id)));

    afterId = page.paging.next ?? undefined;
  } while (afterId);

  return conflictingVenueIds;
};

/**
 * Returns the set of venue IDs that already have a scheduled pricing publish
 * within {@link CONFLICT_WINDOW_MINUTES} of the requested `publicationDate`.
 *
 * The check is skipped (returns an empty set, no request) when there is no
 * publication date (publish-now) or when `enabled` is false.
 */
export const useScheduleConflicts = (publicationDate: Date | null, enabled = true) => {
  const isEnabled = enabled && !!publicationDate;

  const scheduledFrom = publicationDate
    ? dayjs(publicationDate).subtract(CONFLICT_WINDOW_MINUTES, 'minute').toISOString()
    : '';
  const scheduledTo = publicationDate
    ? dayjs(publicationDate).add(CONFLICT_WINDOW_MINUTES, 'minute').toISOString()
    : '';

  const { data, isFetching } = useQuery<Set<string>>({
    queryKey: [getBrandId(), QueryKeys.schedules, 'conflicts', scheduledFrom, scheduledTo],
    queryFn: () => fetchConflictingVenueIds(scheduledFrom, scheduledTo),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
  });

  return {
    conflictingVenueIds: data ?? NO_CONFLICTS,
    isLoading: isEnabled && isFetching,
  };
};
